/**
 * Client-side error beacon.
 *
 * Reports uncaught errors + unhandled promise rejections to
 * /api/csp-report (which uses Workers Logs as the sink). This is the
 * cheapest possible "Sentry-class" coverage — no third-party origin,
 * no extra CSP scope, no extra dependency, ~50 lines.
 *
 * Reports a fingerprint instead of full payloads to bound log volume
 * and avoid leaking long stack traces of user-pasted content. Each
 * fingerprint-message pair is sent once per page load — repeated
 * identical errors do not flood the sink.
 *
 * Wire by calling installErrorBeacon() once at the top of each
 * main-<game>.ts entry point.
 */

const SENT_FINGERPRINTS = new Set<string>();

interface ClientErrorReport {
  kind: "client_error";
  msg: string;
  src?: string;
  line?: number;
  col?: number;
  stack?: string;
  page: string;
  ua: string;
}

function fingerprint(msg: string, src?: string, line?: number): string {
  // Strip query strings + hash to collapse asset-hashed URLs to the same id.
  const srcKey = (src ?? "").replace(/\?.*$/, "").replace(/-[A-Za-z0-9]{8,12}\./, "-HASH.");
  return `${msg}|${srcKey}|${line ?? ""}`;
}

function send(report: ClientErrorReport): void {
  // Use sendBeacon when available so the report survives navigation /
  // tab close. Falls back to fetch with keepalive.
  const body = JSON.stringify(report);
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon("/api/csp-report", blob);
      if (ok) return;
    }
    fetch("/api/csp-report", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      /* swallow — beacon is best-effort by definition */
    });
  } catch {
    /* swallow */
  }
}

let installed = false;

export function installErrorBeacon(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (ev) => {
    const fp = fingerprint(ev.message ?? "(no message)", ev.filename, ev.lineno);
    if (SENT_FINGERPRINTS.has(fp)) return;
    SENT_FINGERPRINTS.add(fp);
    send({
      kind: "client_error",
      msg: (ev.message ?? "").slice(0, 512),
      src: ev.filename,
      line: ev.lineno,
      col: ev.colno,
      stack: ev.error?.stack?.slice(0, 2048),
      page: location.pathname,
      ua: navigator.userAgent.slice(0, 256),
    });
  });

  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    const msg = reason instanceof Error ? reason.message : String(reason ?? "(rejection)");
    const stack = reason instanceof Error ? reason.stack : undefined;
    const fp = fingerprint(msg, "unhandledrejection", 0);
    if (SENT_FINGERPRINTS.has(fp)) return;
    SENT_FINGERPRINTS.add(fp);
    send({
      kind: "client_error",
      msg: `unhandled rejection: ${msg.slice(0, 512)}`,
      stack: stack?.slice(0, 2048),
      page: location.pathname,
      ua: navigator.userAgent.slice(0, 256),
    });
  });
}

// Auto-install on module load. Idempotent (installErrorBeacon guards
// against double-install), so explicit call is also safe.
installErrorBeacon();
